import React, { useState, useEffect } from "react";
import {
    Box,
    Input,
    Select,
    SpaceBetween,
    TokenGroup,
    Container,
    Grid,
    CodeEditor,
    Spinner,
    StatusIndicator,
    Checkbox,
} from "@amzn/awsui-components-react";
import './style.scss'
import 'ace-builds/css/ace.css';
import 'ace-builds/css/theme/cloud_editor.css';
import 'ace-builds/css/theme/cloud_editor_dark.css';
import FlexicoInput from "./FlexicoInput";
import { cei18nStrings } from '../Constants';
import { Parameter } from "src/Ateam-BI/FlexiCo/ParameterDetails/ParameterDetails";

interface CustomMultiValuedParameterRowProps {
    parameter: Parameter,
    onEdit: (updatedParameter: Parameter) => void,
    canDelete: boolean,
    hideLabelPrefix?: string,
    postProcess?: (value: string) => string,
    isCriticalData?: boolean
}

const CustomMultiValuedParameterRow = ({ 
    parameter,
    onEdit, 
    canDelete, 
    hideLabelPrefix,
    postProcess,
    isCriticalData
}: CustomMultiValuedParameterRowProps) => {
    const [ace, setAce] = useState<typeof import('ace-builds') | undefined>(undefined);
    const [ceLoading, setCeLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        async function loadAce() {
            const ace = await import('ace-builds');
            await import('ace-builds/webpack-resolver');
            ace.config.set('useStrictCSP', true);
            return ace;
        }

        loadAce()
            .then(ace => setAce(ace))
            .finally(() => setCeLoading(false));
    }, []);

    const onEditField = (value: any, key: keyof Parameter) => {
        onEdit({ ...parameter, [key]: value });
    };

    const onEditSingleValue = (value: string, key: keyof Parameter) => {
        onEdit({ ...parameter, [key]: [value] });
    };

    const glueJobLoadingComponent = () => (
        <div className="flex_container">
            <Spinner />
            <span className="hintMessage">Checking the existence of Glue Job...</span>
        </div>
    );

    const glueJobNotFoundComponent = () => <span className="error_message">This Glue job does not exist, please retry</span>;

    const enterHintComponent = () => <span className="hintMessage">Press Enter for the values to be considered</span>;

    if (hideLabelPrefix && parameter?.label?.startsWith(hideLabelPrefix)) {
        return null;
    }

    return (
        <Container>
            <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
                <SpaceBetween direction="vertical" size="s">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Select
                            selectedOption={parameter?.field_type ? { label: parameter.field_type, value: parameter.field_type } : null}
                            onChange={({ detail }) => onEditField(detail.selectedOption.value, 'field_type')}
                            options={[
                                { label: "select", value: "select" },
                                { label: "multi_select", value: "multi_select" },
                                { label: "input", value: "input" },
                                { label: "fixed_inputs", value: "fixed_inputs" },
                                { label: "dynamic_select", value: "dynamic_select" },
                                { label: "dynamic_multi_select", value: "dynamic_multi_select" }
                            ]}
                            disabled={!canDelete}
                        />
                        <Input
                            onChange={({ detail }) => {
                                let val = detail.value;
                                if (postProcess) val = postProcess(detail.value);
                                onEditField(val, 'label');
                            }}
                            placeholder="parameter key"
                            value={parameter?.label || ''}
                            disabled={!canDelete}
                        />
                        <Input
                            onChange={({ detail }) => {
                                let val = detail.value;
                                if (postProcess) val = postProcess(detail.value);
                                onEditField(val, 'display_name');
                            }}
                            placeholder="Display name"
                            value={parameter?.display_name || ''}
                            disabled={!canDelete}
                            name="Display name"
                        />
                        {parameter?.field_type !== 'dynamic_select' && parameter?.field_type !== 'dynamic_multi_select' ? (
                            <SpaceBetween direction="vertical" size="m">
                                <div className="param_input">
                                    <FlexicoInput
                                        value={parameter}
                                        index={0} // TODO: Check if we can remove index parameter since it seems its not really being used by FlexicoInput
                                        onEdit={(val: any, idx: number, key: string) => onEditField(val, key)}
                                        enterHintComponent={enterHintComponent}
                                        glueJobLoadingComponent={glueJobLoadingComponent}
                                        glueJobNotFoundComponent={glueJobNotFoundComponent}
                                        setErrorMessage={setErrorMessage}
                                        isCriticalData={isCriticalData}
                                    />
                                </div>
                                <TokenGroup
                                    onDismiss={({ detail: { itemIndex } }) => {
                                        let arr = [...(parameter?.field_tokens || [])];
                                        arr.splice(itemIndex, 1);
                                        onEditField(arr, 'field_tokens');
                                    }}
                                    items={parameter?.field_tokens as Array<{ label: string; dismissLabel: string }> || []}
                                />
                            </SpaceBetween>
                        ) : (
                            <SpaceBetween direction="horizontal" size="s">
                                <Select
                                    selectedOption={parameter?.source_database ? { label: parameter.source_database, value: parameter.source_database } : null}
                                    onChange={({ detail }) => onEditField(detail.selectedOption.value, 'source_database')}
                                    options={[{ label: "FAST", value: "FAST" }]}
                                    disabled={!canDelete}
                                    placeholder="Source data"
                                />
                            </SpaceBetween>
                        )}
                    </SpaceBetween>
                    {parameter?.field_type === 'dynamic_select' || parameter?.field_type === 'dynamic_multi_select' ? (
                        <SpaceBetween direction="horizontal" size="xs">
                            <SpaceBetween direction="vertical" size="s">
                                <div className="param_input_dynamic">
                                    <CodeEditor
                                        ace={ace}
                                        language="sql"
                                        value={parameter?.field_tokens?.[0] as string || ''}
                                        onChange={({ detail }) => onEditSingleValue(detail.value, 'field_tokens')}
                                        preferences={parameter?.preferences || undefined}
                                        onPreferencesChange={e => onEditField(e.detail, 'preferences')}
                                        loading={ceLoading}
                                        i18nStrings={cei18nStrings}
                                        editorContentHeight={200}
                                        themes={{
                                            light: ["cloud_editor"],
                                            dark: ["cloud_editor_dark"]
                                        }}
                                    />
                                </div>
                                <SpaceBetween direction="vertical" size="s">
                                    <span className="small_grey_text_italics">The data will be synced to a cache(athena) database on a daily cadence. Orange account users may need to data-share with Red/Beta accounts.</span>
                                    <span className="small_grey_text_italics">The query must be athena compatible</span>
                                </SpaceBetween>
                            </SpaceBetween>
                            <SpaceBetween direction="vertical" size="s">
                                {parameter?.show_verify_loader && <Box><Spinner />Verifying..</Box>}
                                {parameter?.show_verification_message?.message && (
                                    <StatusIndicator type={parameter.show_verification_message.status}>
                                        {parameter.show_verification_message.message}
                                    </StatusIndicator>
                                )}
                            </SpaceBetween>
                        </SpaceBetween>
                    ) : null}
                    <SpaceBetween direction="horizontal" size="m">
                        <Checkbox
                            checked={parameter?.is_mandatory || false}
                            onChange={({ detail }) => onEditField(detail.checked, 'is_mandatory')}
                            disabled={!canDelete}
                        >
                            Mandatory
                        </Checkbox>
                        <Checkbox
                            checked={parameter?.is_hidden || false}
                            onChange={({ detail }) => onEditField(detail.checked, 'is_hidden')}
                        >
                            Hidden in the execution page
                        </Checkbox>
                    </SpaceBetween>
                </SpaceBetween>
            </Grid>
        </Container>
    );
};

export default CustomMultiValuedParameterRow;
